// 보던 달/주를 URL 쿼리에 유지 — 새로고침, 상세 화면 갔다 돌아오기, 링크 공유 시에도 같은 달/주를 보여준다.
//  - 월 달력: ?month=YYYY-MM   (회원 레슨, 연습실, 내 일정)
//  - 주간 시간표: ?week=YYYY-MM-DD (관리자/선생님 레슨, 그 주 월요일)
// URL이 유일한 상태다. 이번 달/이번 주를 보면 파라미터를 지워 URL을 깔끔하게 두고,
// 이동은 replace로 기록해 뒤로가기가 달/주 단위가 아니라 이전 화면으로 가게 한다.
// 잘못된 값이면 이번 달/이번 주로 대체한다.
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getCurrentWeekStart } from "../components/WeekJumpPicker";

export function monthParam(y, m) {
  const now = dayjs();
  return y === now.year() && m === now.month() ? null : `${y}-${String(m + 1).padStart(2, "0")}`;
}

function parseMonth(raw) {
  const match = /^(\d{4})-(\d{2})$/.exec(raw || "");
  if (!match) return null;
  const m = Number(match[2]) - 1;
  return m >= 0 && m <= 11 ? { year: Number(match[1]), month: m } : null;
}

function parseWeek(raw) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw || "")) return null;
  const d = dayjs(raw);
  if (!d.isValid() || d.format("YYYY-MM-DD") !== raw) return null;
  // 월요일이 아닌 날짜가 오면 그 주 월요일로 맞춘다
  return d.subtract((d.day() + 6) % 7, "day").format("YYYY-MM-DD");
}

function useParam(key) {
  const [searchParams, setSearchParams] = useSearchParams();
  const setParam = useCallback((value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  }, [key, setSearchParams]);
  return [searchParams.get(key), setParam];
}

// [year, month(0~11), setYearMonth(y, m)]
export function useMonthParam() {
  const [raw, setParam] = useParam("month");
  const parsed = parseMonth(raw);
  const year = parsed ? parsed.year : dayjs().year();
  const month = parsed ? parsed.month : dayjs().month();
  const setYearMonth = useCallback((y, m) => setParam(monthParam(y, m)), [setParam]);
  return [year, month, setYearMonth];
}

// [weekStart("YYYY-MM-DD" 월요일), setWeekStart(ws)]
export function useWeekParam() {
  const [raw, setParam] = useParam("week");
  const weekStart = parseWeek(raw) || getCurrentWeekStart();
  const setWeekStart = useCallback(
    (ws) => setParam(ws === getCurrentWeekStart() ? null : ws),
    [setParam]
  );
  return [weekStart, setWeekStart];
}
